import React, { useState, useEffect } from 'react';
import { fmt, api } from '../utils/api';

const STATUS_COLORS = {
  good: '#00d4aa',
  warning: '#fbbf24',
  over: '#ef4444',
};

const DEFAULT_SHOWN = 5;

export default function CategoryBudgetsSnapshot({ onViewAll, refreshKey }) {
  const [data, setData] = useState(null);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    api.getCategoryBudgets().then(setData).catch(() => setData(null));
  }, [refreshKey]);

  if (!data || data.categories.length === 0) return null;

  // Anything over or close to over floats to the top - that's what's
  // actually worth glancing at - then fill remaining slots by spend
  const sorted = [...data.categories].sort((a, b) => {
    const priority = { over: 0, warning: 1, good: 2 };
    if (priority[a.status] !== priority[b.status]) return priority[a.status] - priority[b.status];
    return b.spent - a.spent;
  });

  const needsAttention = sorted.filter((c) => c.status !== 'good').length;
  const shown = expanded ? sorted : sorted.slice(0, DEFAULT_SHOWN);

  return (
    <div style={styles.panel}>
      <div style={styles.header}>
        <h2 style={styles.title}>🗂 Category Budgets</h2>
        {needsAttention > 0 && (
          <span style={styles.attentionBadge}>{needsAttention} need{needsAttention === 1 ? 's' : ''} attention</span>
        )}
      </div>

      <div style={styles.list}>
        {shown.map((cat) => {
          const color = STATUS_COLORS[cat.status];
          const barPct = cat.budget > 0 ? Math.min((cat.spent / cat.budget) * 100, 100) : 0;
          return (
            <div key={cat.category} style={styles.row}>
              <div style={styles.rowTop}>
                <span style={styles.catName}>{cat.category}</span>
                <span style={{ color, fontWeight: '700', fontSize: '0.72rem' }}>
                  {fmt.currency(cat.spent)} / {fmt.currency(cat.budget)}
                </span>
              </div>
              <div style={styles.barTrack}>
                <div style={{ ...styles.barFill, width: `${barPct}%`, background: color }} />
              </div>
            </div>
          );
        })}
      </div>

      <div style={styles.footer}>
        {sorted.length > DEFAULT_SHOWN && (
          <button style={styles.expandBtn} onClick={() => setExpanded((e) => !e)}>
            {expanded ? 'Show less' : `Show all ${sorted.length}`}
          </button>
        )}
        {onViewAll && (
          <button style={styles.viewAllBtn} onClick={onViewAll}>
            Manage budgets →
          </button>
        )}
      </div>
    </div>
  );
}

const styles = {
  panel: {
    background: '#111827',
    border: '1px solid #1e2a3a',
    borderRadius: '16px',
    padding: '1.25rem',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '0.85rem',
    flexWrap: 'wrap',
    gap: '6px',
  },
  title: {
    margin: 0,
    fontSize: '0.9rem',
    fontWeight: '700',
    color: '#fff',
    fontFamily: "'Syne', sans-serif",
  },
  attentionBadge: {
    color: '#ef4444',
    fontSize: '0.65rem',
    fontWeight: '700',
    border: '1px solid #ef4444',
    borderRadius: '999px',
    padding: '2px 8px',
    fontFamily: "'Syne', sans-serif",
  },
  list: { display: 'flex', flexDirection: 'column', gap: '0.6rem' },
  row: {},
  rowTop: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '4px',
  },
  catName: {
    color: '#9ca3af',
    fontSize: '0.75rem',
    fontFamily: "'Syne', sans-serif",
  },
  barTrack: {
    height: '4px',
    background: '#0a0f1e',
    borderRadius: '3px',
    overflow: 'hidden',
  },
  barFill: {
    height: '100%',
    borderRadius: '3px',
  },
  footer: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginTop: '0.85rem',
  },
  expandBtn: {
    background: 'transparent',
    border: 'none',
    color: '#6b7280',
    fontSize: '0.7rem',
    fontWeight: '600',
    cursor: 'pointer',
    fontFamily: "'Syne', sans-serif",
    padding: 0,
  },
  viewAllBtn: {
    background: 'transparent',
    border: 'none',
    color: '#00d4aa',
    fontSize: '0.72rem',
    fontWeight: '600',
    cursor: 'pointer',
    fontFamily: "'Syne', sans-serif",
    padding: 0,
    marginLeft: 'auto',
  },
};
